import { useRef, type ComponentProps, type FormEvent, type ReactNode } from 'react';

import { ApiFailureError, type ApiFailure } from '../../lib/http/errors';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../ui/dialog';
import { Spinner } from '../../ui/spinner';
import { cn } from '../../ui/utils';
import { ErrorState } from '../state/ErrorState';
import { st } from '../state/messages';
import { ft } from './messages';

/** The vendored `DialogFooter` paints a tinted full-bleed bar (`bg-secondary`,
 *  `border-t`) for a generic dialog; frame 67:144's footer sits on the
 *  dialog's own surface with no divider, so every dialog sharing that frame
 *  renders through this instead of repeating the override per caller
 *  (`cn`'s tailwind-merge resolves each pair by conflict group, not source
 *  order, so `bg-card`/`border-t-0` win regardless of where `DialogFooter`
 *  puts its own defaults). */
export function DialogSurfaceFooter({
  className,
  ...props
}: ComponentProps<typeof DialogFooter>): ReactNode {
  return <DialogFooter className={cn('border-t-0 bg-card', className)} {...props} />;
}

/** A failure that names a field renders on that field (`fieldErrorOf`); every
 *  other failure renders once, at the top of the form. */
function generalFailure(failure: ApiFailure | null): ApiFailure | null {
  if (failure === null) return null;
  if (failure.kind === 'invalid' && failure.field !== null) return null;
  return failure;
}

export function FormDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  submitLabel: string;
  cancelLabel?: string;
  pending: boolean;
  /** Disables submit for a required field left blank, distinct from `pending`. */
  submitDisabled?: boolean;
  failure: ApiFailure | ApiFailureError | null;
  /** Returning the mutation's promise (`mutateAsync`) hands the close to this dialog: it closes
   *  once the promise resolves, stays open on a rejection (the caller's `failure` renders it),
   *  and refuses a second submit while the first is in flight. A `void` return leaves the close
   *  to the caller. */
  onSubmit: (event: FormEvent<HTMLFormElement>) => void | Promise<unknown>;
  children: ReactNode;
  trigger?: ReactNode;
  /** Radix's own default (`true`) traps focus and hides the rest of the page
   *  from assistive tech -- right for a form opened over other content, wrong
   *  for one that is itself the destination of a direct navigation and needs
   *  to coexist with the shell around it (`../../app/tenant/request/
   *  RequestCredits.tsx`, reached at its own route). */
  modal?: boolean;
  /** Passed through to `DialogContent`'s own `scrim` -- ships the frame's
   *  dimming for a `modal={false}` caller reached by direct navigation
   *  (`../../app/tenant/capacity/RequestCredits.tsx`, frame `67:144`).
   *  `onPointerDownOutside` rides along so the scrim itself never dismisses
   *  the dialog, same as a non-modal `DialogContent` used directly. */
  scrim?: boolean;
  /** Rendered first inside the footer, `mr-auto` so it pushes Cancel/Submit
   *  to their usual right-aligned spot instead of widening the footer's own
   *  `justify-end` (frames 86:2, 304:11029's "N créditos/dia" left of the
   *  submit button). Absent leaves the footer exactly as every other caller
   *  already has it. */
  footerStart?: ReactNode;
  /** `DialogContent`'s width; absent keeps its default. */
  size?: ComponentProps<typeof DialogContent>['size'];
}): ReactNode {
  const failure = props.failure instanceof ApiFailureError ? props.failure.failure : props.failure;
  const general = generalFailure(failure);
  const inFlight = useRef(false);
  // Radix renders DialogContent through a portal, so this form is DOM-detached from wherever
  // FormDialog is mounted but stays a descendant in the React fiber tree -- React's synthetic
  // event system bubbles `submit` along that tree, not the DOM, so an unguarded submit here also
  // fires an ancestor `<form onSubmit>` when a caller opens this dialog from inside its own form
  // (`../../app/tenant/capacity/RequestSlots.tsx`, opened from `../../app/campaign/record/
  // NewCampaignDialog.tsx`'s own create-campaign form).
  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.stopPropagation();
    // `pending` only reaches the submit button after React Query's notify tick and a render, so a
    // second Enter or click inside that gap would otherwise start a second write.
    if (inFlight.current) {
      event.preventDefault();
      return;
    }
    const settled = props.onSubmit(event);
    if (settled === undefined) return;
    inFlight.current = true;
    void settled
      .then(
        () => props.onOpenChange(false),
        // The caller's `failure` already renders the rejection; the dialog stays open on it.
        () => undefined,
      )
      .finally(() => {
        inFlight.current = false;
      });
  }
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} modal={props.modal}>
      {props.trigger !== undefined && <DialogTrigger asChild>{props.trigger}</DialogTrigger>}
      <DialogContent
        size={props.size}
        scrim={props.scrim}
        onPointerDownOutside={props.scrim === true ? (event) => event.preventDefault() : undefined}
      >
        <form onSubmit={handleSubmit} className="flex flex-col gap-6" noValidate>
          <DialogHeader>
            <DialogTitle>{props.title}</DialogTitle>
            {props.description !== undefined && (
              <DialogDescription>{props.description}</DialogDescription>
            )}
          </DialogHeader>
          {general !== null && <ErrorState failure={general} />}
          <div className="flex flex-col gap-4">{props.children}</div>
          <DialogSurfaceFooter>
            {props.footerStart}
            <Button
              type="button"
              variant="outline"
              onClick={() => props.onOpenChange(false)}
              disabled={props.pending}
            >
              {props.cancelLabel ?? ft('cancel')}
            </Button>
            {/* An explicit name keeps the status role's own label out of this
             * button's name-from-content ("CarregandoCriar") while the
             * spinner still announces on its own. */}
            <Button
              type="submit"
              disabled={props.pending || props.submitDisabled === true}
              aria-label={props.submitLabel}
            >
              {props.pending && <Spinner aria-label={st('loading')} />}
              {props.submitLabel}
            </Button>
          </DialogSurfaceFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
